import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VolumeTrendPoint } from "@/lib/volume-balance";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface VolumeTrendChartProps {
  weeklyTrends: VolumeTrendPoint[];
  monthlyTrends: VolumeTrendPoint[];
}

export function VolumeTrendChart({ weeklyTrends, monthlyTrends }: VolumeTrendChartProps) {
  const [view, setView] = useState<"weekly" | "monthly">("weekly");
  const [metric, setMetric] = useState<"sets" | "volume">("sets");

  const data = view === "weekly" ? weeklyTrends : monthlyTrends;
  
  // Calculate trend
  const calculateTrend = () => {
    if (data.length < 2) return { direction: "neutral", percentage: 0 };
    
    const recentPeriods = data.slice(-4);
    const olderPeriods = data.slice(-8, -4);
    
    if (olderPeriods.length === 0) return { direction: "neutral", percentage: 0 };
    
    const recentAvg = recentPeriods.reduce((sum, p) => sum + p[metric], 0) / recentPeriods.length;
    const olderAvg = olderPeriods.reduce((sum, p) => sum + p[metric], 0) / olderPeriods.length;
    
    if (olderAvg === 0) return { direction: "neutral", percentage: 0 };
    
    const change = ((recentAvg - olderAvg) / olderAvg) * 100;
    
    return {
      direction: change > 5 ? "up" : change < -5 ? "down" : "neutral",
      percentage: Math.abs(change),
    };
  };

  const trend = calculateTrend();

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload as VolumeTrendPoint;
      return (
        <div className="rounded-sm border border-border bg-card px-2 py-1.5 text-[13px] shadow-sm">
          <div className="text-muted-foreground">{point.label}</div>
          <div className="font-mono tabular-nums space-y-0.5">
            <div><span className="text-muted-foreground">Sets:</span> {point.sets}</div>
            <div><span className="text-muted-foreground">Volume:</span> {point.volume.toLocaleString()} lbs</div>
            <div><span className="text-muted-foreground">Workouts:</span> {point.workouts}</div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Volume Trends</CardTitle>
          <CardDescription>Track your training volume over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="chart-height-md flex items-center justify-center text-muted-foreground">
            Not enough workout data to show trends yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Volume Trends</CardTitle>
            <CardDescription>Track training volume over time</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {trend.direction === "up" && (
              <div className="flex items-center gap-1 text-success text-sm">
                <TrendingUp className="h-4 w-4" />
                <span>+{trend.percentage.toFixed(0)}%</span>
              </div>
            )}
            {trend.direction === "down" && (
              <div className="flex items-center gap-1 text-destructive text-sm">
                <TrendingDown className="h-4 w-4" />
                <span>-{trend.percentage.toFixed(0)}%</span>
              </div>
            )}
            {trend.direction === "neutral" && (
              <div className="flex items-center gap-1 text-muted-foreground text-sm">
                <Minus className="h-4 w-4" />
                <span>Stable</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Tabs value={view} onValueChange={(v) => setView(v as "weekly" | "monthly")} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="weekly" className="text-xs px-3 h-6">Weekly</TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs px-3 h-6">Monthly</TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={metric} onValueChange={(m) => setMetric(m as "sets" | "volume")} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="sets" className="text-xs px-3 h-6">Sets</TabsTrigger>
              <TabsTrigger value="volume" className="text-xs px-3 h-6">Volume</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <div className="chart-height-md" role="img" aria-label="Training volume trend over time">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) =>
                  metric === "volume" && value >= 1000
                    ? `${(value / 1000).toFixed(0)}k`
                    : value
                }
              />
              <Tooltip cursor={false} content={<CustomTooltip />} animationDuration={150} />
              <Area
                type="monotone"
                dataKey={metric}
                stroke="hsl(var(--primary))"
                strokeWidth={1.5}
                fill="url(#volumeGradient)"
                dot={false}
                activeDot={{ r: 3, strokeWidth: 0, fill: "hsl(var(--primary))" }}
                animationDuration={350}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
